import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Check, Clock, X, Minus } from "lucide-react";

const STATUS_META = {
  invited: { label: "Invited", cls: "bg-emerald-600 text-white", icon: Check },
  alternate: { label: "Alternate", cls: "bg-amber-500 text-white", icon: Clock },
  rejected: { label: "Rejected", cls: "bg-slate-400 text-white", icon: X },
  none: { label: "Undecided", cls: "bg-white border border-slate-300 text-slate-500", icon: Minus },
};

const TOP_N = 40;

export default function CampSelection() {
  const { campId } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/camps/${campId}/selection`).then(({ data }) => setData(data)).catch(() => setData({ players: [], summary: {} }));
  }, [campId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (player, status) => {
    try {
      await api.patch(`/admin/camps/${campId}/selection/${player.player_id}`, { status });
      setData((d) => ({
        ...d,
        players: d.players.map((p) => (p.player_id === player.player_id ? { ...p, selection_status: status } : p)),
        summary: recomputeSummary(d.players.map((p) => (p.player_id === player.player_id ? { ...p, selection_status: status } : p))),
      }));
      toast.success(`${player.first_name} → ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  if (!data) return <AdminLayout><div className="p-8 label-overline">Loading selection board…</div></AdminLayout>;

  const s = data.summary || {};

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="label-overline text-[#E63946] mt-3">Top {TOP_N} Selection</div>
        <h1 data-testid="selection-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{data.camp?.title || "Camp"}</h1>
        <p className="text-xs text-slate-500 mt-1">Ranked by CBR Overall Rating (verified metrics + evaluations). Invite to Final Selection Camp, mark Alternate, or Reject.</p>

        <CampOpsNav campId={campId} />

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SummaryTile testid="sel-sum-invited" label="Invited" val={s.invited || 0} cls="text-emerald-700" />
          <SummaryTile testid="sel-sum-alternate" label="Alternates" val={s.alternate || 0} cls="text-amber-700" />
          <SummaryTile testid="sel-sum-rejected" label="Rejected" val={s.rejected || 0} cls="text-slate-500" />
          <SummaryTile testid="sel-sum-undecided" label="Undecided" val={s.undecided || 0} cls="text-[#0A192F]" />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline w-12">Rank</th>
                <th className="text-left p-3 label-overline">Player</th>
                <th className="text-left p-3 label-overline">Pos</th>
                <th className="text-left p-3 label-overline">Class</th>
                <th className="text-left p-3 label-overline">CBR Rating</th>
                <th className="text-left p-3 label-overline">Status</th>
                <th className="text-right p-3 label-overline">Decision</th>
              </tr>
            </thead>
            <tbody>
              {data.players.map((p) => {
                const meta = STATUS_META[p.selection_status] || STATUS_META.none;
                return (
                  <React.Fragment key={p.player_id}>
                    {p.rank === TOP_N + 1 && (
                      <tr><td colSpan="7" className="bg-[#0A192F] text-white text-[10px] uppercase tracking-widest text-center py-1">— Top {TOP_N} Cutoff —</td></tr>
                    )}
                    <tr data-testid={`selection-row-${p.player_id}`} className={`border-b border-slate-100 last:border-0 ${p.rank <= TOP_N ? "" : "opacity-70"}`}>
                      <td className="p-3 font-data text-lg">{p.rank}</td>
                      <td className="p-3">
                        <Link to={`/prospects/${p.slug}`} className="font-semibold hover:text-[#E63946]">{p.first_name} {p.last_name}</Link>
                        <div className="text-[11px] text-slate-500">{p.province || "—"}</div>
                      </td>
                      <td className="p-3 font-data">{p.primary_position || "—"}</td>
                      <td className="p-3 font-data">{p.graduation_year || "—"}</td>
                      <td className="p-3 font-data text-2xl text-[#0A192F]">{p.cbr_overall_rating != null ? Number(p.cbr_overall_rating).toFixed(1) : "—"}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${meta.cls}`}>
                          <meta.icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <DecBtn testid={`invite-${p.player_id}`} active={p.selection_status === "invited"} activeCls="bg-emerald-600 text-white" onClick={() => setStatus(p, "invited")}>Invite</DecBtn>
                          <DecBtn testid={`alt-${p.player_id}`} active={p.selection_status === "alternate"} activeCls="bg-amber-500 text-white" onClick={() => setStatus(p, "alternate")}>Alt</DecBtn>
                          <DecBtn testid={`reject-${p.player_id}`} active={p.selection_status === "rejected"} activeCls="bg-slate-500 text-white" onClick={() => setStatus(p, "rejected")}>Reject</DecBtn>
                          {p.selection_status !== "none" && (
                            <DecBtn testid={`clear-${p.player_id}`} active={false} onClick={() => setStatus(p, "none")}>Clear</DecBtn>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {data.players.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-sm text-slate-500">No players registered for this camp.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function recomputeSummary(players) {
  return {
    invited: players.filter((p) => p.selection_status === "invited").length,
    alternate: players.filter((p) => p.selection_status === "alternate").length,
    rejected: players.filter((p) => p.selection_status === "rejected").length,
    undecided: players.filter((p) => p.selection_status === "none").length,
    total: players.length,
  };
}

function SummaryTile({ label, val, cls, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-4 rounded-sm">
      <div className="label-overline">{label}</div>
      <div className={`font-data text-3xl mt-1 ${cls}`}>{val}</div>
    </div>
  );
}

function DecBtn({ children, onClick, active, activeCls = "bg-[#0A192F] text-white", testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${active ? activeCls : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}
    >
      {children}
    </button>
  );
}
